import BaseChart from "@/classes/BaseChart";
import { chartMetrics, weightedMap } from "@/constants";
import { TYPE_AVERAGE, TYPE_SUM } from "@/helpers/constants";
import {
  allocateByPeriods,
  buildPeriodsMonth,
  buildPeriodsQuarter,
  buildPeriodsWeek,
  isoKey,
  mEnd,
  mStart,
  qEnd,
  qStart,
  toDate,
  wEnd,
  wStart,
} from "@/helpers/timeBuckets";
import { campaignKpiPlanningStageMapping, channelObjectiveMap } from "@/mockData/planCreateMockData";
import { LOWERCASE_OTHER_VALUE } from "@/settings/constants";
import { DateGroupingConfig, chartGroupingConfig, timeChartGroupingConfig } from "@/settings/groupingConfig";
import planTableMetrics from "@/settings/metrics/planTableMetrics";
import { ProjectOutputFilterParams, ScenarioNode } from "@/settings/projectOutputFilters";
import summaryTableMetrics from "@/settings/table/summaryTableMetrics";
import { FAMILY } from "@/settings/widgetFamilies";
import { BusinessGoal, OutputRecord, Plan, Project, Scenario } from "@/types/project";

type FlatRow = Record<string, string | number | null>;
export type ChartDataRecord = Record<string, string | number | boolean | null | undefined>;
type SummaryDataItem = Record<string, number | null>;

export type ChartSettings = {
  [key: string]: unknown;
  locale?: string;
  formatAs?: "string" | "number";
};

type GroupingConf = {
  groupByFromSettings: string[];
  valuesFromSettings: string[];
  percentileDistribution?: boolean;
  dataGranularitySource?: string;
  topNFromSettings?: string;
  rankByFromSettings?: string;
};

type CampaignScenarioNode = {
  scenario_id: string | null;
  scenario: string | null;
  start_date: string | null;
  end_date: string | null;
  geography: string;
  rows: FlatRow[];
};

type CampaignPlanNode = {
  plan: string | null;
  campaign_id: string | null;
  children: CampaignScenarioNode[];
};

type CampaignTreeNode = {
  business_goal: string | null;
  children: CampaignPlanNode[];
};

type PercentileOptions = {
  locale?: string;
  formatAs?: "string" | "number";
  maxFractionDigits?: number;
};

const GROUP_KEY_SEP = "|";

const normValue = (value: unknown) => `${value ?? ""}`.toString().trim().toLowerCase();

const sameValue = (a: unknown, b: unknown) => normValue(a) === normValue(b);

const isAllSelected = (values?: string[] | null) =>
  !Array.isArray(values) || values.length === 0 || values.some((value) => sameValue(value, "All"));

const collectScenarioIds = (scenario?: ProjectOutputFilterParams["scenario"]): Set<string> | null => {
  if (!Array.isArray(scenario) || scenario.length === 0) return null;
  if (scenario.some((item) => typeof item === "string" && sameValue(item, "All"))) return null;
  const ids = new Set<string>();

  const visit = (node?: ScenarioNode | string) => {
    if (!node) return;
    if (typeof node === "string") {
      const strId = node.trim();
      if (strId) ids.add(strId);
      return;
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach(visit);
      return;
    }

    if (node.id) {
      const strId = String(node.id).trim();
      if (strId) ids.add(strId);
    }
  };

  scenario.forEach(visit);
  return ids.size ? ids : null;
};

const roundN = (n: number, frac = 4) => Math.round((n + Number.EPSILON) * Math.pow(10, frac)) / Math.pow(10, frac);
const METRIC_CALC: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  summaryTableMetrics.forEach((d) => {
    if (d?.value) m[String(d.value)] = d.calculationType || TYPE_SUM;
  });
  return m;
})();
const calcTypeOf = (key: string) => METRIC_CALC[key] || TYPE_SUM;

const baseKeysFor = (metrics: string[]) => {
  const base = new Set<string>();
  for (const m of metrics) {
    const w = weightedMap[m];
    if (w) {
      if (w.num) base.add(w.num);
      if (w.denom) base.add(w.denom);
    } else {
      base.add(m);
    }
  }
  return base;
};

const computeMetricValue = (metric: string, sums: Record<string, number>, counts: Record<string, number>): number => {
  const w = weightedMap[metric];

  if (w) {
    const den = Number(sums[w.denom] || 0);
    return den ? ((w.multiplier || 1) * Number(sums[w.num] || 0)) / den : 0;
  }

  if (calcTypeOf(metric) === TYPE_AVERAGE) {
    const c = counts[metric] || 0;
    return c ? (sums[metric] || 0) / c : 0;
  }

  return Number(sums[metric] || 0);
};

const applyTopNWithOther = (
  rows: ChartDataRecord[],
  dimension: string,
  rankMetric: string,
  showItems: number
): ChartDataRecord[] => {
  const neededBase = baseKeysFor([rankMetric]);
  const isAverage = calcTypeOf(rankMetric) === TYPE_AVERAGE;
  const totals = new Map<string, { sums: Record<string, number>; counts: Record<string, number> }>();

  for (const row of rows) {
    const key = String(row[dimension] ?? "");
    let acc = totals.get(key);
    if (!acc) {
      acc = { sums: Object.create(null), counts: Object.create(null) };
      totals.set(key, acc);
    }

    for (const k of neededBase) {
      const v = Number(row[k]);
      if (Number.isFinite(v)) acc.sums[k] = (acc.sums[k] || 0) + v;
    }

    if (isAverage) {
      const v = Number(row[rankMetric]);
      if (Number.isFinite(v)) acc.counts[rankMetric] = (acc.counts[rankMetric] || 0) + 1;
    }
  }

  if (totals.size <= showItems) {
    return rows;
  }

  const kept = new Set(
    Array.from(totals.entries())
      .map(([key, { sums, counts }]) => ({ key, rank: computeMetricValue(rankMetric, sums, counts) }))
      .sort((a, b) => b.rank - a.rank)
      .slice(0, showItems)
      .map((item) => item.key)
  );

  return rows.map((row) =>
    kept.has(String(row[dimension] ?? "")) ? row : { ...row, [dimension]: LOWERCASE_OTHER_VALUE }
  );
};

const OVERTIME_RANK_FIELDS: Record<string, string> = {
  channel_budget: "budget",
  impressions: "impressions",
};

const overtimeTopNKeys = (
  rows: BudgetPoint[],
  dimension: string,
  rankMetric: string,
  showItems: number
): Set<string> | null => {
  const rankField = OVERTIME_RANK_FIELDS[rankMetric];
  if (!rankField) return null;

  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = String((row as unknown as Record<string, unknown>)[dimension] ?? "");
    const v = Number((row as unknown as Record<string, unknown>)[rankField]);
    totals.set(key, (totals.get(key) || 0) + (Number.isFinite(v) ? v : 0));
  }

  if (totals.size <= showItems) return null;

  return new Set(
    Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, showItems)
      .map(([key]) => key)
  );
};

const makeGroupKey = (row: ChartDataRecord, groupBy: string[]) => groupBy.map((k) => String(row[k] ?? "")).join("_");
const makeNumberFormatter = (opts: PercentileOptions) => {
  const { locale, formatAs, maxFractionDigits = 4 } = opts;
  const nf =
    formatAs === "string"
      ? new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: maxFractionDigits,
          useGrouping: false,
        })
      : null;

  return (n: number): number | string => (nf ? nf.format(roundN(n, maxFractionDigits)) : roundN(n, maxFractionDigits));
};

// const selectMetricSettings = (settings: ChartSettings): Record<string, unknown> => {
//   const result: Record<string, unknown> = {};
//   for (const key of displaySettingsNames) {
//     const value = settings[key];
//     if (value === undefined || value === null || value === "") continue;
//     result[key] = value;
//   }
//   return result;
// };

const toMidnightUTC = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const addDaysUTC = (d: Date, days: number) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));

const normalizeBudgetKeyPart = (value?: string | null): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();
const buildBudgetKey = (scenarioId: string, kpi: string, channel: string): string =>
  `${normalizeBudgetKeyPart(scenarioId)}|${normalizeBudgetKeyPart(kpi)}|${normalizeBudgetKeyPart(channel)}`;

const buildScenarioCpmLookup = (scenario: Scenario | null): Map<string, number> => {
  const lookup = new Map<string, number>();
  if (!scenario) return lookup;
  const scenarioId = String(scenario.id ?? "");
  for (const record of scenario.output?.data ?? []) {
    const key = buildBudgetKey(
      scenarioId,
      String(record.campaign_kpi ?? record.kpi ?? ""),
      String(record.channel ?? "")
    );
    const rawCpm = Number(record.cpm ?? 0);
    if (!Number.isFinite(rawCpm)) continue;
    if (!lookup.has(key)) lookup.set(key, rawCpm);
  }
  return lookup;
};

const buildScenarioOutputLookup = (scenario: Scenario | null): Map<string, OutputRecord> => {
  const lookup = new Map<string, OutputRecord>();
  if (!scenario) return lookup;
  const scenarioId = String(scenario.id ?? "");
  for (const record of scenario.output?.data ?? []) {
    const key = buildBudgetKey(
      scenarioId,
      String(record.campaign_kpi ?? record.kpi ?? ""),
      String(record.channel ?? "")
    );
    if (!lookup.has(key)) lookup.set(key, record);
  }
  return lookup;
};

type BudgetPoint = {
  scenarioId: string;
  scenarioName: string;
  scenario: string;
  scenario_id: string;
  planName: string;
  plan: string;
  goalName: string;
  business_goal: string;
  business_goal_property_value: string;
  b2b_b2c: string;
  campaign_id: string;
  kpi: string;
  campaign_kpi: string;
  channel: string;
  channel_objective: string;
  funnel_stage: string;
  media_type: string;
  day: string;
  budget: number;
  cpm: number;
  impressions: number;
  scenarioStart: string;
  scenarioEnd: string;
};

const buildDailyBudgetRecords = (project: Project | null): BudgetPoint[] => {
  if (!project) return [];
  const records: BudgetPoint[] = [];

  for (const goal of project.business_goals ?? []) {
    for (const plan of goal.plans ?? []) {
      for (const scenario of plan.scenarios ?? []) {
        const scenarioStart = toDate(scenario.start_date);
        const scenarioEnd = toDate(scenario.end_date);
        if (!scenarioStart || !scenarioEnd) continue;

        const normalizedScenarioStart = toMidnightUTC(scenarioStart);
        const normalizedScenarioEnd = toMidnightUTC(scenarioEnd);

        const scenarioCpmLookup = buildScenarioCpmLookup(scenario);
        const scenarioOutputLookup = buildScenarioOutputLookup(scenario);
        for (const budgetItem of scenario.output?.budget_in_time ?? []) {
          const kpi = budgetItem.kpi ?? "";
          const channel = budgetItem.channel ?? "";
          if (!kpi || !channel) continue;

          for (const dist of budgetItem.distribution ?? []) {
            const distStart = toDate(dist.start_date);
            const distEnd = toDate(dist.end_date);
            if (!distStart || !distEnd) continue;
            const normalizedDistStart = toMidnightUTC(distStart);
            const normalizedDistEnd = toMidnightUTC(distEnd);

            const intervalStart =
              normalizedDistStart > normalizedScenarioStart ? normalizedDistStart : normalizedScenarioStart;
            const intervalEnd = normalizedDistEnd < normalizedScenarioEnd ? normalizedDistEnd : normalizedScenarioEnd;
            if (intervalStart > intervalEnd) continue;

            for (let current = intervalStart; current <= intervalEnd; current = addDaysUTC(current, 1)) {
              const dailyBudget = Number(dist.daily_budget) || 0;
              const keyDay = isoKey(current);
              const recordKey = buildBudgetKey(String(scenario.id ?? ""), kpi, channel);
              const cpmValue = scenarioCpmLookup.get(recordKey) ?? 0;
              const outputRecord = scenarioOutputLookup.get(recordKey);
              const outputPlan = String(outputRecord?.plan ?? "");
              const outputScenarioId = String(outputRecord?.scenario_id ?? "");
              const outputCampaignKpi = String(outputRecord?.campaign_kpi ?? kpi ?? "");
              const outputChannel = String(outputRecord?.channel ?? channel ?? "");
              const impressions = cpmValue > 0 ? 1000 * (dailyBudget / cpmValue) : 0;
              records.push({
                scenarioId: scenario.id ?? "",
                scenarioName: scenario.name ?? "",
                scenario: scenario.name ?? "",
                scenario_id: outputScenarioId || scenario.id || "",
                planName: plan.name ?? "",
                plan: outputPlan || plan.name || "",
                goalName: goal.name ?? "",
                business_goal: String(outputRecord?.business_goal ?? goal.name ?? ""),
                business_goal_property_value: String(goal.business_goal ?? ""),
                b2b_b2c: String(goal.b2b_or_b2c ?? ""),
                campaign_id: String(plan.campaign_id ?? ""),
                kpi,
                campaign_kpi: outputCampaignKpi,
                channel,
                channel_objective: resolveChannelObjective(outputChannel, channelObjectiveMap) ?? "",
                funnel_stage: resolveFunnelStage(outputCampaignKpi, campaignKpiPlanningStageMapping) ?? "",
                media_type: String(outputRecord?.media_type ?? ""),
                day: keyDay,
                budget: dailyBudget,
                cpm: cpmValue,
                impressions,
                scenarioStart: scenario.start_date ?? "",
                scenarioEnd: scenario.end_date ?? "",
              });
            }
          }
        }
      }
    }
  }

  return records;
};

const quantile = (sorted: number[], p: number): number => {
  if (!sorted.length) return 0;
  const pos = (sorted.length - 1) * p;
  const base = Math.floor(pos);
  const rest = pos - base;
  const next = sorted[base + 1];
  return next !== undefined ? sorted[base] + rest * (next - sorted[base]) : sorted[base];
};

export function collectGroupValues(
  rows: ChartDataRecord[],
  groupBy: string[],
  metrics: string[]
): Map<string, { keys: Record<string, string | null>; buckets: Record<string, number[]> }> {
  const map = new Map<string, { keys: Record<string, string | null>; buckets: Record<string, number[]> }>();
  if (!groupBy.length || !metrics.length) return map;

  for (const row of rows) {
    const keyParts = groupBy.map((k) => (row[k] == null ? null : String(row[k])));
    const key = keyParts.map((v) => String(v ?? "")).join(GROUP_KEY_SEP);

    let acc = map.get(key);
    if (!acc) {
      const keys: Record<string, string | null> = {};
      groupBy.forEach((k, i) => (keys[k] = keyParts[i]));
      acc = { keys, buckets: {} };
      map.set(key, acc);
    }

    for (const m of metrics) {
      const n = Number(row[m]);
      if (Number.isFinite(n)) (acc.buckets[m] ??= []).push(n);
    }
  }

  return map;
}

export function percentilesFromGroupValues(
  grouped: Map<string, { keys: Record<string, string | null>; buckets: Record<string, number[]> }>,
  opts: PercentileOptions = {}
): ChartDataRecord[] {
  const fmt = makeNumberFormatter(opts);
  const out: ChartDataRecord[] = [];

  for (const { keys, buckets } of grouped.values()) {
    const rec: ChartDataRecord = { ...keys };
    for (const m of Object.keys(buckets)) {
      const arr = buckets[m].slice().sort((a, b) => a - b);
      const v25 = quantile(arr, 0.25);
      const v50 = quantile(arr, 0.5);
      const v75 = quantile(arr, 0.75);
      const vmin = arr.length ? arr[0] : 0;
      const vmax = arr.length ? arr[arr.length - 1] : 0;

      rec[`${m}_min`] = fmt(vmin);
      rec[`${m}_25th`] = fmt(v25);
      rec[`${m}_median`] = fmt(v50);
      rec[`${m}_75th`] = fmt(v75);
      rec[`${m}_max`] = fmt(vmax);
    }
    out.push(rec);
  }

  return out;
}

const safeNum = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const safeStr = (v: unknown): string | null => (v === undefined || v === null ? null : String(v));
const resolveFunnelStage = (kpi: unknown, map: Record<string, string>): string | null => {
  if (kpi == null) return null;
  const key = String(kpi);
  return map[key] ?? null;
};
const resolveChannelObjective = (channel: unknown, map: Record<string, string>): string | null => {
  if (channel == null) return null;
  const key = String(channel);
  return map[key] ?? null;
};

export function flattenProjectToRows(project: Project): ChartDataRecord[] {
  const flat: ChartDataRecord[] = [];

  for (const goal of project.business_goals ?? []) {
    for (const plan of goal.plans ?? []) {
      for (const scenario of plan.scenarios ?? []) {
        const rows = scenario?.output?.data ?? [];
        if (!Array.isArray(rows) || rows.length === 0) continue;

        for (const r of rows) {
          const row: ChartDataRecord = {
            id: safeStr(r.id),
            b2b_b2c: safeStr(goal.b2b_or_b2c),
            business_goal: safeStr(goal.name),
            business_goal_property_value: safeStr(goal.business_goal),
            campaign_id: safeStr(plan.campaign_id),
            campaign_kpi: safeStr(r.campaign_kpi),
            channel: safeStr(r.channel),
            channel_objective: resolveChannelObjective(r.channel, channelObjectiveMap),
            funnel_stage: resolveFunnelStage(r.campaign_kpi, campaignKpiPlanningStageMapping),
            media_type: safeStr(r.media_type),
            plan: safeStr(plan.name),
            scenario_id: safeStr(r.scenario_id ?? scenario.id),
            scenario: safeStr(scenario.name),
            start_date: safeStr(r.start_date ?? scenario.start_date),
            end_date: safeStr(r.end_date ?? scenario.end_date),
            channel_budget: safeNum(r.flight_budget),
            channel_percentage: safeNum(r.flight_percentage),
          };

          for (const [outKey, srcKey] of Object.entries(chartMetrics)) {
            row[outKey] = safeNum(r[srcKey]);
          }

          flat.push(row);
        }
      }
    }
  }

  return flat;
}

export default class ProjectWidgetApi {
  async getSummaryData(project: Project, metrics: string[]): Promise<SummaryDataItem[]> {
    const rows: Record<string, number>[] = [];
    for (const goal of project.business_goals ?? []) {
      for (const plan of goal.plans ?? []) {
        for (const scenario of plan.scenarios ?? []) {
          for (const record of scenario.output?.data ?? []) {
            rows.push({
              ...(record as Record<string, number>),
              channel_budget: Number(record.flight_budget ?? 0),
              channel_percentage: Number(record.flight_percentage ?? 0),
            });
          }
        }
      }
    }

    const result: SummaryDataItem = {};

    for (const metric of metrics) {
      const wInfo = weightedMap[metric];
      if (wInfo) {
        const numSum = rows.reduce((s, r) => s + (Number(r[wInfo.num]) || 0), 0);
        const denomSum = rows.reduce((s, r) => s + (Number(r[wInfo.denom]) || 0), 0);
        result[metric] =
          numSum && denomSum ? (wInfo.multiplier ? (wInfo.multiplier * numSum) / denomSum : numSum / denomSum) : 0;
      } else {
        const sum = rows.reduce((s, r) => s + (Number(r[metric]) || 0), 0);
        result[metric] = sum;
      }
    }

    return [result];
  }

  getPlanSummaryData(project: Project): Array<Record<string, string | number | null>> {
    const rowsByPlan = new Map<
      string,
      {
        plan: string;
        start_date: string;
        end_date: string;
        budget: number;
      }
    >();

    for (const goal of project.business_goals ?? []) {
      for (const plan of goal.plans ?? []) {
        for (const scenario of plan.scenarios ?? []) {
          const planName = String(plan.name ?? "").trim();
          const planId = String(plan.id ?? "").trim();
          const planKey = planId || planName;
          if (!planKey || !planName) continue;

          const outputBudget = (scenario.output?.data ?? []).reduce((sum, record) => {
            return sum + Number(record.flight_budget ?? record.channel_budget ?? record.budget ?? 0);
          }, 0);
          const scenarioBudget = Number(scenario.net_budget ?? 0);
          const budget = scenarioBudget || outputBudget;

          const current = rowsByPlan.get(planKey);
          if (!current) {
            rowsByPlan.set(planKey, {
              plan: planName,
              start_date: String(scenario.start_date ?? ""),
              end_date: String(scenario.end_date ?? ""),
              budget,
            });
            continue;
          }

          current.budget += budget;
          if (!current.start_date || (scenario.start_date && String(scenario.start_date) < current.start_date)) {
            current.start_date = String(scenario.start_date ?? "");
          }
          if (!current.end_date || (scenario.end_date && String(scenario.end_date) > current.end_date)) {
            current.end_date = String(scenario.end_date ?? "");
          }
        }
      }
    }

    const rows = Array.from(rowsByPlan.values());
    const totalBudget = rows.reduce((sum, row) => sum + row.budget, 0);

    return rows
      .map((row) => ({
        plan: row.plan,
        start_date: row.start_date,
        end_date: row.end_date,
        budget: row.budget,
        channel_percentage: totalBudget > 0 ? (100 * row.budget) / totalBudget : 0,
      }))
      .sort((a, b) => String(a.plan).localeCompare(String(b.plan)));
  }

  async getChartData(project: Project, ctx: BaseChart): Promise<ChartDataRecord[]> {
    let flat = flattenProjectToRows(project);

    const pf = ctx?.settings?.filterByScenario;
    if (pf !== undefined) {
      flat = flat.filter((row) => row.scenario === pf);
    }

    const settings = (ctx?.settings ?? {}) as ChartSettings;
    const isStr = (v: unknown): v is string => typeof v === "string" && v.length > 0;

    const conf = (chartGroupingConfig as Record<string, GroupingConf | undefined>)[ctx.chartName];
    if (!conf) return [];

    const groupBy = conf.groupByFromSettings.map((k) => settings[k]).filter(isStr);
    const values = conf.valuesFromSettings.flatMap((k) => {
      const v = settings[k];
      return Array.isArray(v) ? (v as unknown[]).filter(isStr) : isStr(v) ? [v] : [];
    });

    const showItems = Number(settings.showItems);
    const topNDimension = conf.topNFromSettings ? settings[conf.topNFromSettings] : undefined;
    const rankMetric = conf.rankByFromSettings ? settings[conf.rankByFromSettings] : values[0];

    if (!ctx.isDualMode && Number.isFinite(showItems) && showItems > 0 && isStr(topNDimension) && isStr(rankMetric)) {
      flat = applyTopNWithOther(flat, topNDimension, rankMetric, showItems);
    }

    if (conf.percentileDistribution && groupBy.length === conf.groupByFromSettings.length && values.length > 0) {
      const grouped = collectGroupValues(flat, groupBy, values);
      return percentilesFromGroupValues(grouped, {
        locale: isStr(settings.locale) ? settings.locale : undefined,
        formatAs: settings.formatAs === "string" ? "string" : "number",
        maxFractionDigits: 4,
      });
    }

    if (!(groupBy.length === conf.groupByFromSettings.length && values.length > 0)) return [];

    const neededBase = baseKeysFor(values);

    const needAvg = new Set(values.filter((m) => calcTypeOf(m) === TYPE_AVERAGE));

    type Acc = {
      keys: Record<string, string | null>;
      sums: Record<string, number>;
      counts: Record<string, number>;
    };
    const accMap = new Map<string, Acc>();

    for (const r of flat) {
      const gKey = makeGroupKey(r, groupBy);
      let acc = accMap.get(gKey);
      if (!acc) {
        const keys: Record<string, string | null> = {};
        groupBy.forEach((k) => (keys[k] = r[k] == null ? null : String(r[k])));
        acc = { keys, sums: Object.create(null), counts: Object.create(null) };
        accMap.set(gKey, acc);
      }

      for (const k of neededBase) {
        const v = Number(r[k]);
        if (Number.isFinite(v)) acc.sums[k] = (acc.sums[k] || 0) + v;
      }

      for (const m of needAvg) {
        const v = Number(r[m]);
        if (Number.isFinite(v)) {
          acc.sums[m] = (acc.sums[m] || 0) + v;
          acc.counts[m] = (acc.counts[m] || 0) + 1;
        }
      }
    }

    const nf = makeNumberFormatter({
      locale: isStr(settings.locale) ? settings.locale : undefined,
      formatAs: settings.formatAs === "string" ? "string" : "number",
      maxFractionDigits: 4,
    });

    const aggregated: ChartDataRecord[] = [];
    for (const { keys, sums, counts } of accMap.values()) {
      const out: ChartDataRecord = { ...keys };
      for (const m of values) {
        out[m] = nf(computeMetricValue(m, sums, counts));
      }
      aggregated.push(out);
    }

    if (groupBy.length > 0) {
      const firstKey = groupBy[0];
      const collator = isStr(settings.locale) ? new Intl.Collator(settings.locale) : undefined;
      aggregated.sort((a, b) => {
        const av = a[firstKey],
          bv = b[firstKey];
        if (typeof av === "string" && typeof bv === "string") {
          return collator ? collator.compare(av, bv) : av.localeCompare(bv);
        }
        const an = Number(av) || 0,
          bn = Number(bv) || 0;
        return an - bn;
      });
    }

    return aggregated;
  }

  private filterProjectBySelection(
    project: Project | null,
    filters?: ProjectOutputFilterParams | null
  ): Project | null {
    if (!project) return null;

    const businessGoalFilter = filters?.businessGoalPropertyValue || [];
    const planFilter = filters?.plan || [];
    const scenarioIds = collectScenarioIds(filters?.scenario);

    const matchBusinessGoal = (goal: BusinessGoal) =>
      isAllSelected(businessGoalFilter) ||
      businessGoalFilter.some((value) => sameValue(value, goal.name) || sameValue(value, goal.id));

    const matchPlan = (plan: Plan) =>
      isAllSelected(planFilter) || planFilter.some((value) => sameValue(value, plan.name) || sameValue(value, plan.id));

    const filteredBusinessGoals = (project.business_goals || [])
      .filter((goal) => matchBusinessGoal(goal))
      .map((goal) => {
        const filteredPlans = (goal.plans || [])
          .filter((plan) => matchPlan(plan))
          .map((plan) => {
            const filteredScenarios = (plan.scenarios || []).filter((scenario) => {
              if (!scenarioIds) return true;
              return scenario.id ? scenarioIds.has(String(scenario.id)) : false;
            });

            return { ...plan, scenarios: filteredScenarios };
          })
          .filter((plan) => (plan.scenarios?.length || 0) > 0 || scenarioIds === null);

        return { ...goal, plans: filteredPlans };
      })
      .filter((goal) => (goal.plans?.length || 0) > 0 || scenarioIds === null);

    return { ...project, business_goals: filteredBusinessGoals };
  }

  async getChartDataOvertime(
    project: Project,
    ctx: BaseChart,
    filters?: ProjectOutputFilterParams | null
  ): Promise<ChartDataRecord[]> {
    const isOvertime = ctx.chartFamily === FAMILY.OVERTIME;

    if (!isOvertime) {
      return this.getChartData(project, ctx);
    }

    const filteredProject = this.filterProjectBySelection(project, filters);
    const settings = (ctx?.settings ?? {}) as ChartSettings;
    const timeConf = (timeChartGroupingConfig as Record<string, DateGroupingConfig | undefined>)[ctx.chartName];
    const granularityKey = timeConf?.dataGranularitySource?.replace(/^settings\./, "") ?? "";
    const groupByKeysRaw = timeConf?.groupByFromSettings;
    const groupByKeys = Array.isArray(groupByKeysRaw)
      ? groupByKeysRaw
      : typeof groupByKeysRaw === "string" && groupByKeysRaw
      ? [groupByKeysRaw]
      : [];
    const resolvedGroupBy = groupByKeys
      .map((k) => (typeof settings[k] === "string" ? { key: k, value: String(settings[k]) } : null))
      .filter((v): v is { key: string; value: string } => !!v);
    const hasTimeMetric = granularityKey && typeof settings[granularityKey] === "string";
    const timeKey = hasTimeMetric ? String(settings[granularityKey]) : "month";

    const dailyBudgets = buildDailyBudgetRecords(filteredProject);
    const aggregatedByDay = new Map<string, { impressions: number; channel_budget: number; groupByValue?: string }>();
    const primaryGroupByKey = resolvedGroupBy[0]?.value;

    const showItems = Number(settings.showItems);
    const rankMetric = timeConf?.rankByFromSettings ? settings[timeConf.rankByFromSettings] : undefined;
    const keptGroups =
      !ctx.isDualMode &&
      primaryGroupByKey &&
      Number.isFinite(showItems) &&
      showItems > 0 &&
      typeof rankMetric === "string" &&
      rankMetric
        ? overtimeTopNKeys(dailyBudgets, primaryGroupByKey, rankMetric, showItems)
        : null;

    for (const entry of dailyBudgets) {
      const dayKey = entry.day;
      const dayDate = toDate(dayKey);
      const periodKey =
        dayDate && timeKey === "week"
          ? isoKey(wStart(dayDate))
          : dayDate && timeKey === "quarter"
          ? isoKey(qStart(dayDate))
          : dayDate && timeKey === "month"
          ? isoKey(mStart(dayDate))
          : dayKey;
      const rawGroupByValue = primaryGroupByKey
        ? String((entry as Record<string, unknown>)[primaryGroupByKey] ?? "")
        : "";

      const groupByValue = keptGroups && !keptGroups.has(rawGroupByValue) ? LOWERCASE_OTHER_VALUE : rawGroupByValue;
      const aggKey = primaryGroupByKey ? `${periodKey}||${groupByValue}` : periodKey;
      const acc = aggregatedByDay.get(aggKey) ?? {
        impressions: 0,
        channel_budget: 0,
        ...(primaryGroupByKey ? { groupByValue } : {}),
      };
      acc.impressions += Number(entry.impressions) || 0;
      acc.channel_budget += Number(entry.budget) || 0;
      aggregatedByDay.set(aggKey, acc);
    }

    // Stacked area sits on a continuous DateAxis with connected series: periods without data
    // produce no point, so an empty interval gets bridged to the next one instead of dropping to
    // zero. Backfill a zero record for every missing period x stack value so gaps read as zero.
    if (ctx.chartName === "stackedAreaChart" && dailyBudgets.length) {
      const periodBuilder =
        timeKey === "week"
          ? buildPeriodsWeek
          : timeKey === "quarter"
          ? buildPeriodsQuarter
          : timeKey === "month"
          ? buildPeriodsMonth
          : null;

      if (periodBuilder) {
        let minDay = dailyBudgets[0].day;
        let maxDay = dailyBudgets[0].day;
        for (const entry of dailyBudgets) {
          if (entry.day < minDay) minDay = entry.day;
          if (entry.day > maxDay) maxDay = entry.day;
        }

        const rangeStart = toDate(minDay);
        const rangeEnd = toDate(maxDay);

        if (rangeStart && rangeEnd) {
          const groupValues = primaryGroupByKey
            ? Array.from(new Set(Array.from(aggregatedByDay.values()).map((v) => v.groupByValue ?? "")))
            : [""];

          for (const period of periodBuilder(rangeStart, rangeEnd)) {
            for (const groupByValue of groupValues) {
              const aggKey = primaryGroupByKey ? `${period.key}||${groupByValue}` : period.key;
              if (!aggregatedByDay.has(aggKey)) {
                aggregatedByDay.set(aggKey, {
                  impressions: 0,
                  channel_budget: 0,
                  ...(primaryGroupByKey ? { groupByValue } : {}),
                });
              }
            }
          }
        }
      }
    }

    const t: ChartDataRecord[] = Array.from(aggregatedByDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, totals]) => {
        const day = primaryGroupByKey ? key.split("||")[0] : key;
        const out: ChartDataRecord = {
          [timeKey]: day,
          impressions: totals.impressions,
          channel_budget: totals.channel_budget,
        };
        if (primaryGroupByKey) {
          out[primaryGroupByKey] = totals.groupByValue ?? "";
        }
        return out;
      });
    return t;
  }

  flattenCampaignDataTree(project: Project): CampaignTreeNode[] {
    const extraRateMap: Record<string, { num: string; denom: string }> = {
      ctr: { num: "clicks", denom: "impressions" },
      vcr: { num: "completed_views", denom: "video_views" },
    };

    const tree = [];

    for (const goal of project.business_goals ?? []) {
      const plans = [];

      for (const plan of goal.plans ?? []) {
        const scenarios = [];

        for (const scenario of plan.scenarios ?? []) {
          const outputData = scenario.output?.data ?? [];

          const groups = new Map<string, typeof outputData>();
          for (const record of outputData) {
            const key = `${record.campaign_kpi ?? ""}|${record.channel ?? ""}|${record.media_type ?? ""}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(record);
          }

          const rows: FlatRow[] = [];

          for (const flights of groups.values()) {
            const first = flights[0];
            const totalBudget = flights.reduce((s, f) => s + Number(f.flight_budget ?? 0), 0);
            const totalPct = flights.reduce((s, f) => s + Number(f.flight_percentage ?? 0), 0);

            const sums: Record<string, number> = { channel_budget: totalBudget };
            for (const [k, v] of Object.entries(first)) {
              if (k === "flight_budget" || k === "flight_percentage") continue;
              if (typeof v === "number") {
                sums[k] = flights.reduce((s, f) => s + Number((f as Record<string, unknown>)[k] ?? 0), 0);
              }
            }

            for (const [metric, info] of Object.entries({ ...weightedMap, ...extraRateMap })) {
              const num = sums[info.num] ?? 0;
              const denom = sums[info.denom] ?? 0;
              const mult = (weightedMap[metric] as { multiplier?: number } | undefined)?.multiplier ?? 1;
              sums[metric] = num && denom ? (mult * num) / denom : 0;
            }

            const row: FlatRow = {};

            for (const { value } of planTableMetrics) {
              if (value === "start_date") {
                row[value] =
                  flights
                    .map((f) => String(f.start_date ?? ""))
                    .filter(Boolean)
                    .sort()[0] ?? "";
              } else if (value === "end_date") {
                row[value] =
                  flights
                    .map((f) => String(f.end_date ?? ""))
                    .filter(Boolean)
                    .sort()
                    .reverse()[0] ?? "";
              } else if (value === "geography") {
                row[value] = (scenario.states || []).join(", ");
              } else if (value === "budget") {
                row[value] = totalBudget;
              } else if (value === "channel_percentage") {
                row[value] = totalPct;
              } else {
                row[value] =
                  sums[value] !== undefined
                    ? sums[value]
                    : ((first as Record<string, unknown>)[value] as string | number | null);
              }
            }

            row.channel = first.channel ?? "";
            row.media_type = first.media_type ?? "";
            row.campaign_kpi = first.campaign_kpi ?? "";
            row.channel_budget = totalBudget;
            row.flight_count = flights.length;
            if (flights.length === 1) {
              (row as Record<string, unknown>).id = String(first.id ?? "");
              (row as Record<string, unknown>).flight_budget = totalBudget;
              (row as Record<string, unknown>).flight_percentage = totalPct;
            }
            (row as Record<string, unknown>).flights = flights.map((f) => {
              const flightRow: Record<string, unknown> = {
                id: String(f.id ?? ""),
                start_date: String(f.start_date ?? ""),
                end_date: String(f.end_date ?? ""),
                budget: String(f.flight_budget ?? 0),
                channel_percentage: String(f.flight_percentage ?? 0),
              };

              for (const { value } of planTableMetrics) {
                if (
                  value === "start_date" ||
                  value === "end_date" ||
                  value === "budget" ||
                  value === "channel_percentage" ||
                  value === "campaign_id" ||
                  value === "business_goal" ||
                  value === "plan" ||
                  value === "geography" ||
                  value === "channel" ||
                  value === "campaign_kpi"
                ) {
                  continue;
                }

                flightRow[value] = String((f as Record<string, unknown>)[value] ?? 0);
              }

              return flightRow;
            });

            rows.push(row);
          }

          scenarios.push({
            scenario_id: scenario.id,
            scenario: scenario.name,
            start_date: scenario.start_date,
            end_date: scenario.end_date,
            geography: (scenario.states || []).join(", "),
            rows,
          });
        }

        plans.push({ plan: plan.name, campaign_id: plan.campaign_id, children: scenarios });
      }

      tree.push({
        business_goal: goal.name,
        children: plans,
      });
    }
    return tree as CampaignTreeNode[];
  }

  buildGlobalRange(project: Project) {
    let gStart: Date | null = null,
      gEnd: Date | null = null;

    for (const goal of project.business_goals ?? []) {
      for (const plan of goal.plans ?? []) {
        for (const sc of plan.scenarios ?? []) {
          const s = toDate(sc.start_date);
          const e = toDate(sc.end_date);
          if (s && (!gStart || s < gStart)) gStart = s;
          if (e && (!gEnd || e > gEnd)) gEnd = e;

          for (const item of sc.output?.budget_in_time ?? []) {
            for (const d of item.distribution ?? []) {
              const ds = toDate(d.start_date);
              const de = toDate(d.end_date);
              if (ds && (!gStart || ds < gStart)) gStart = ds;
              if (de && (!gEnd || de > gEnd)) gEnd = de;
            }
          }
        }
      }
    }
    return { gStart, gEnd };
  }

  flattenByGeneric(
    project: Project,
    makePeriods: (start: Date, end: Date) => { start: Date; end: Date; key: string }[],
    clampRange: (s: Date, e: Date) => { s: Date; e: Date }
  ) {
    const tree: CampaignTreeNode[] = [];
    const { gStart, gEnd } = this.buildGlobalRange(project);
    if (!gStart || !gEnd) return tree;

    const { s: S, e: E } = clampRange(gStart, gEnd);
    const periods = makePeriods(S, E);

    for (const goal of project.business_goals ?? []) {
      const plans = [];
      for (const plan of goal.plans ?? []) {
        const scenarios = [];

        for (const sc of plan.scenarios ?? []) {
          const geography = (sc.states || sc.dma || []).join(", ");
          const rows = [];

          for (const item of sc.output?.budget_in_time ?? []) {
            const { per, total, minStart, maxEnd } = allocateByPeriods(periods, item.distribution ?? []);

            if (!total || !minStart || !maxEnd) continue;

            const row = {
              campaign_id: plan.campaign_id ?? "",
              business_goal: goal.name ?? "",
              plan: plan.name ?? "",
              geography,
              channel: item.channel ?? "",
              campaign_kpi: item.kpi ?? "Other",
              channel_budget: total,
              start_date: minStart.toISOString().slice(0, 10),
              end_date: maxEnd.toISOString().slice(0, 10),
              ...per,
            };

            rows.push(row);
          }

          scenarios.push({
            scenario_id: sc.id,
            scenario: sc.name,
            start_date: sc.start_date,
            end_date: sc.end_date,
            geography,
            rows,
          });
        }

        plans.push({ plan: plan.name, campaign_id: plan.campaign_id, children: scenarios });
      }

      tree.push({ business_goal: goal.name, children: plans });
    }

    return tree;
  }

  flattenCampaignDataTreeByMonth(project: Project) {
    return this.flattenByGeneric(project, buildPeriodsMonth, (s, e) => ({ s: mStart(s), e: mEnd(e) }));
  }

  flattenCampaignDataTreeByQuarter(project: Project) {
    return this.flattenByGeneric(project, buildPeriodsQuarter, (s, e) => ({ s: qStart(s), e: qEnd(e) }));
  }

  flattenCampaignDataTreeByWeek(project: Project) {
    return this.flattenByGeneric(project, buildPeriodsWeek, (s, e) => ({ s: wStart(s), e: wEnd(e) }));
  }

  async getScenarioMetricsAll(project: Project): Promise<Array<Record<string, number | string>>> {
    const slugify = (str: string) =>
      (str || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    const scenarioOrder: string[] = [];
    const seen = new Set<string>();
    const sumByScenario: Map<string, Record<string, number>> = new Map();
    const cntByScenario: Map<string, Record<string, number>> = new Map();
    const metricsSet = new Set<string>();

    const ensure = (name: string) => {
      if (!seen.has(name)) {
        seen.add(name);
        scenarioOrder.push(name);
      }
      if (!sumByScenario.has(name)) sumByScenario.set(name, Object.create(null));
      if (!cntByScenario.has(name)) cntByScenario.set(name, Object.create(null));
      return { sums: sumByScenario.get(name)!, cnts: cntByScenario.get(name)! };
    };

    const add = (sums: Record<string, number>, cnts: Record<string, number>, key: string, v: unknown) => {
      if (typeof v === "number" && Number.isFinite(v)) {
        sums[key] = (sums[key] || 0) + v;
        cnts[key] = (cnts[key] || 0) + 1;
        metricsSet.add(key);
      }
    };
    if (project && Array.isArray(project.business_goals)) {
      for (const bg of project.business_goals || []) {
        for (const pl of bg.plans || []) {
          for (const sc of pl.scenarios || []) {
            const scName = (sc?.name || "").toString().trim();
            if (!scName) continue;

            const { sums, cnts } = ensure(scName);
            const rows = (sc?.output?.data || []) as Array<Record<string, string | number | null | undefined>>;

            for (const r of rows) {
              const budget = r.flight_budget ?? r.channel_budget ?? r.amount;
              add(sums, cnts, "channel_budget", budget);

              for (const [k, v] of Object.entries(r)) {
                if (k === "flight_budget" || k === "flight_percentage" || k === "channel_budget" || k === "amount")
                  continue;
                add(sums, cnts, k, v);
              }
            }
          }
        }
      }
    }

    const out: Array<Record<string, number | string>> = [];
    const keysOrdered = [
      ...(["channel_budget"].filter((k) => metricsSet.has(k)) as string[]),
      ...Array.from(metricsSet)
        .filter((k) => k !== "channel_budget")
        .sort(),
    ];

    for (const metric of keysOrdered) {
      const row: Record<string, number | string> = { metric };
      const calcType = calcTypeOf(metric);
      const wInfo = weightedMap[metric];

      for (const s of scenarioOrder) {
        const sums = sumByScenario.get(s) || {};
        const cnts = cntByScenario.get(s) || {};

        if (wInfo) {
          const numSum = sums[wInfo.num] || 0;
          const denomSum = sums[wInfo.denom] || 0;
          row[`scenario_${slugify(s)}`] =
            numSum && denomSum ? (wInfo.multiplier ? (wInfo.multiplier * numSum) / denomSum : numSum / denomSum) : 0;
        } else if (calcType === TYPE_AVERAGE) {
          row[`scenario_${slugify(s)}`] = (cnts[metric] || 0) > 0 ? sums[metric] / cnts[metric] : 0;
        } else {
          row[`scenario_${slugify(s)}`] = sums[metric] || 0;
        }
      }

      out.push(row);
    }

    return out;
  }
}
